import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

// Extend Day.js with required plugins
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Parses a date string as a UTC Day.js object.
 * @param {string} dateString
 * @returns {dayjs.Dayjs}
 */
export const parseUTCDate = (dateString) => dayjs.utc(dateString).local();

/**
 * Parses an ISO date/datetime field and returns a dayjs object or null if invalid.
 * @param {string} value - The date or datetime string in ISO 8601 format.
 * @returns {dayjs.Dayjs|null} - Parsed dayjs object or null if invalid.
 */
export const parseISODateField = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

/**
 * Checks if a date is within a specified date range.
 * @param {dayjs.Dayjs} date
 * @param {dayjs.Dayjs} start
 * @param {dayjs.Dayjs} end
 * @returns {boolean}
 */
export const isDateInRange = (date, start, end) =>
  date.isSameOrAfter(start) && date.isSameOrBefore(end);

/**
 * Converts milliseconds to an object containing hours, minutes, and seconds.
 * @param {number} milliseconds
 * @returns {{hours: number, minutes: number, seconds: number}}
 */
export const millisecondsToTime = (milliseconds) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { hours, minutes, seconds };
};

export const timeFormat = (time) => {
  let number = time / 1; //force number casting
  if (Number.isNaN(number)) {
    return '--:--:--';
  }
  let sign = '';
  if (number < 0) {
    sign = '-';
  }
  // negative time shall be shown as: - hh:mm:ss
  number = Math.abs(number);
  let hours = Math.floor(number / 3600) % 24; // convert to hours
  let minutes = Math.floor(number / 60) % 60; // convert to minutes
  let seconds = Math.round(number % 60);
  hours = hours < 10 ? `0${hours}` : hours;
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  seconds = seconds < 10 ? `0${seconds}` : seconds;
  return `${sign} ${hours}:${minutes}:${seconds}`;
};

/**
 * Formats the date to ISO 8601 string.
 * @param {Date} date - The selected date.
 * @param {Boolean} withTime - Whether to include time in the format.
 * @returns {String} Formatted date string.
 */
export const formatDate = (date, withTime) =>
  withTime
    ? dayjs(date).format('YYYY-MM-DDTHH:mm:ss')
    : dayjs(date).format('YYYY-MM-DD');
